import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SESSION_TIMEOUT = 20 * 60 * 1000; // 20 minutes hard limit
const WARNING_BEFORE = 2 * 60 * 1000;   // Warn 2 minutes before

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('brain_gym_token'));
  const [loading, setLoading] = useState(true);
  const tokenRef = useRef(token);
  const timeoutRef = useRef(null);
  const warningRef = useRef(null);

  // Keep ref in sync
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: API,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return instance;
  }, [token]);

  const logout = useCallback((redirect = true) => {
    clearTimeout(timeoutRef.current);
    clearTimeout(warningRef.current);
    localStorage.removeItem('brain_gym_token');
    localStorage.removeItem('brain_gym_login_time');
    setToken(null);
    setUser(null);
    if (redirect) {
      window.location.replace('/login');
    }
  }, []);

  // Hard session timer — starts at login, never resets on activity
  const startSessionTimer = useCallback(() => {
    if (!tokenRef.current) return;

    clearTimeout(timeoutRef.current);
    clearTimeout(warningRef.current);

    const loginTime = parseInt(localStorage.getItem('brain_gym_login_time') || '0', 10);
    const now = Date.now();
    const elapsed = now - loginTime;
    const remaining = SESSION_TIMEOUT - elapsed;

    if (remaining <= 0) {
      toast.error('Your 20-minute session has ended. Time for a screen break!');
      logout();
      return;
    }

    const warningAt = remaining - WARNING_BEFORE;
    if (warningAt > 0) {
      warningRef.current = setTimeout(() => {
        if (tokenRef.current) {
          toast.warning('2 minutes left! Your session will end soon.', { duration: 10000 });
        }
      }, warningAt);
    }

    timeoutRef.current = setTimeout(() => {
      if (tokenRef.current) {
        toast.error('Your 20-minute session has ended. Time for a screen break!');
        logout();
      }
    }, remaining);
  }, [logout]);

  // Start timer when token exists
  useEffect(() => {
    if (!token) return;
    startSessionTimer();
    return () => {
      clearTimeout(timeoutRef.current);
      clearTimeout(warningRef.current);
    };
  }, [token, startSessionTimer]);

  const fetchUser = useCallback(async () => {
    if (!tokenRef.current) {
      setLoading(false);
      return;
    }
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('brain_gym_token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // COPPA: Disable PostHog analytics for children under 13
  useEffect(() => {
    if (typeof window.posthog !== 'undefined' && user) {
      if (user.age && user.age < 13) {
        window.posthog.opt_out_capturing();
      } else {
        window.posthog.opt_in_capturing();
      }
    }
  }, [user]);

  const login = async (name, password) => {
    const response = await api.post('/auth/login', { name, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('brain_gym_token', newToken);
    localStorage.setItem('brain_gym_login_time', Date.now().toString());
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const signup = async (name, password, age, enrollment_code) => {
    const response = await api.post('/auth/signup', { name, password, age, enrollment_code });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('brain_gym_token', newToken);
    localStorage.setItem('brain_gym_login_time', Date.now().toString());
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const signupSubscribe = async (data) => {
    const response = await api.post('/auth/signup-subscribe', data);
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('brain_gym_token', newToken);
    localStorage.setItem('brain_gym_login_time', Date.now().toString());
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const refreshUser = async () => {
    await fetchUser();
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, signup, signupSubscribe, logout, updateUser, refreshUser, api }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
